import React, { Component } from 'react';
import axios from 'axios';
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withRouter } from 'react-router-dom';
import { sortBy } from 'lodash';
import moment from 'moment';
import LaunchIcon from '@material-ui/icons/Launch';
// material
import { Grid, Container, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Drawer } from '@material-ui/core';
// app
import { updateSession, setApiUrls, addAuth, openSnackbar, closeSnackbar } from '../../store/action-creators';
// containers
import AdminContainer from '../../containers/Admin';
import Loader from '../../containers/Loader';
// models
import { Auth, Session } from '../../entities/auth';
import { APIUrl, Campaign, CampaignContact, emptyCampaignContact } from '../../entities/site';
import { User } from '../../entities/user';
import { MessageTemplate, Message, MessageGroup, emptyMessageGroup } from '../../entities/notification';
// components
import Loading from '../../components/site/Loading';
import ActionButton from '../../components/buttons/ActionButton';
import SelectInput, { SelectItem } from '../../components/inputs/SelectInput';
// services
import AuthService from '../../services/auth';

const useProd = true;

const styles: any = {
  tableWrapper: {
    maxHeight: 440,
    overflow: 'auto',
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    paddingTop: 10,
    paddingBottom: 20,
    textAlign: 'left',
    display: 'flex'
  },
  sectionTitle: {
    fontSize: 14,
    textAlign: 'center'
  },
  stat: {
    fontSize: 48,
    textAlign: 'center',
    color: '#00ABCA',
    fontWeight: 'bold'
  },
  link: {
    color: '#00ABCA',
    cursor: 'pointer'
  },
  border: {
    paddingTop: 40,
    paddingBottom: 40,
    borderRight: '1px solid #CCC'
  },
  normal: {
    paddingTop: 40,
    paddingBottom: 40,
  },
  footer: {
    fontSize: 12,
    textAlign: 'right',
    paddingTop: 5,
    color: '#9e9e9e'
  },
  messageDetails: {
    padding: 20,
    borderBottom: '2px solid #F4F4F5',
    marginBottom: 20
  },
  messageContainer: {
    maxHeight: 400,
    overFlow: 'scroll'
  },
  message: {

  },
  messageDate: {
    color: '#9e9e9e',
    fontSize: 11
  },
  toMessageContainer: {
    marginBottom: 20
  },
  fromMessageContainer: {
    marginBottom: 20
  },
  toMessage: {
    backgroundColor: '#E4F5F8',
    borderRadius: 4,
    padding: 10,
    textAlign: 'left'
  },
  fromMessage: {
    backgroundColor: '#F4F4F5',
    borderRadius: 4,
    padding: 10,
    textAlign: 'left'
  }
}

const dateFilterList = [
  {
    name: '1 week',
    value: moment().subtract(1, 'week').valueOf().toString()
  },
  {
    name: '1 month',
    value: moment().subtract(1, 'month').valueOf().toString()
  },
  {
    name: '3 months',
    value: moment().subtract(3, 'month').valueOf().toString()
  }
];

interface AdminPageProps {
  history: any;
  auth: Auth;
  session: Session;
  apiUrls: APIUrl;
  updateSession(session: Session): void;
  setApiUrls(urls: APIUrl): void;
  openSnackbar(message: string): void;
  closeSnackbar(): void;
}

interface AdminPageState {
  loadingStats: boolean;
  loadingContacts: boolean;
  loadingDrawer: boolean;
  showDrawer: boolean;
  campaigns: Campaign[];
  users: User[];
  templates: MessageTemplate[];
  contacts: CampaignContact[];
  selectedContact: CampaignContact;
  lastKey: any;
  dateFilters: SelectItem[];
  dateFilter: string;
  messages: Message[];
  messageGroup: MessageGroup;
}

class AdminPage extends Component<AdminPageProps, AdminPageState> {

  constructor(props: AdminPageProps) {
    super(props);
    this.state = {
      loadingStats: false,
      loadingContacts: false,
      loadingDrawer: false,
      showDrawer: false,
      campaigns: [],
      users: [],
      templates: [],
      contacts: [],
      selectedContact: emptyCampaignContact,
      lastKey: null,
      dateFilters: dateFilterList,
      dateFilter: dateFilterList[0].value,
      messages: [],
      messageGroup: emptyMessageGroup
    }
  }

  componentDidMount = async () => {
    // fetch stuff
    this.fetchAllStats();
    this.fetchRecentContacts(this.state.dateFilter);
  }

  fetchAllStats = async () => {

    try {

      this.setState({ loadingStats: true });

      const campaignsRes = await axios.get(`${AuthService.getApiUrls().site}/campaigns?status=active&limit=500`);
      const usersRes = await axios.get(`${AuthService.getApiUrls().user}/users`);
      const templateRes = await axios.get(`${AuthService.getApiUrls().notifications}/message/templates`);

      this.setState({
        loadingStats: false,
        campaigns: campaignsRes.data.results,
        users: usersRes.data.results,
        templates: templateRes.data
      });

    } catch (error) {
      this.setState({ loadingStats: true });
    }

  }

  fetchRecentContacts = async (startDate: string) => {

    try {

      this.setState({ loadingContacts: true });

      let params = `?status=clickThrough&limit=50&startDate=${startDate}`;
      if (this.state.lastKey) {
        params += `&keyId=${this.state.lastKey.keyId}`;
        params += `&keyStatus=${this.state.lastKey.keyStatus}`;
        params += `&keyCreatedDate=${this.state.lastKey.keyCreatedDate}`;
      }
      const contactsRes = await axios.get(`${AuthService.getApiUrls().site}/campaign/contacts${params}`);

      let allContacts = this.state.contacts;
      allContacts = allContacts.concat(contactsRes.data.results);
      allContacts = sortBy(allContacts, 'createdDate').reverse();

      this.setState({
        loadingContacts: false,
        contacts: allContacts,
        lastKey: contactsRes.data.lastKey
      })

    } catch (error) {
      this.setState({ loadingContacts: false });
    }

  }

  handleOpenMessage = async (contact: CampaignContact) => {

    try {

      // open drawer
      this.setState({
        showDrawer: true,
        loadingDrawer: true,
        selectedContact: contact
      });

      // get collaborators
      // const campaignCollaboratorRes = await axios.get(`${AuthService.getApiUrls().site}/campaign/${contact.campaignId}/collaborators`);

      // get message group
      const messageGroupRes = await axios.get(`${AuthService.getApiUrls().notifications}/message/group/contact/${contact.id}`);
      if (messageGroupRes.data) {

        // get messages
        const messages = await axios.get(`${AuthService.getApiUrls().notifications}/messages/group/${messageGroupRes.data.id}`);
        if (messages.data) {
          const sortedMessages = sortBy(messages.data, 'createdDate');
          this.setState({
            messages: sortedMessages,
            messageGroup: messageGroupRes.data
          });
        }
      }

      this.setState({ loadingDrawer: false });
    } catch (error) {
      console.log('error', error);
    }

  }


  toggleDrawer = () => {
    this.setState({
      showDrawer: false,
      selectedContact: emptyCampaignContact
    });
  }

  handleDateChange = (value: string) => {
    this.setState({ dateFilter: value });
    this.fetchRecentContacts(value);
  }

  handleContactClick = (contact: CampaignContact) => {
    this.props.history.push(`/campaign/${contact.campaignId}/contact/${contact.id}`);
  }

  render() {

    return (
      <AdminContainer showOption={false} private={false} maxWidth={false}>

        <Container maxWidth="lg">
          <Loader open={this.state.loadingStats} >
            <div style={styles.title}>Admin Stats</div>
            <Grid container>
              <Grid item xs={3}>
                <div style={styles.border}>
                  <div style={styles.sectionTitle}>Active Campaigns</div>
                  <div style={styles.stat}>{(this.state.campaigns.length > 500 ? '500+' : this.state.campaigns.length)}</div>
                </div>
              </Grid>
              <Grid item xs={3}>
                <div style={styles.border}>
                  <div style={styles.sectionTitle}>Users</div>
                  <div style={styles.stat}>{this.state.users.length}</div>
                </div>
              </Grid>
              <Grid item xs={3}>
                <div style={styles.normal}>
                  <div style={styles.sectionTitle}>Templates</div>
                  <div style={styles.stat}>{this.state.templates.length}</div>
                </div>
              </Grid>
            </Grid>
          </Loader>
          <Loader open={this.state.loadingContacts}>
            <div style={styles.title}>
              <div style={{ paddingTop: 10 }}>Campaign Clicks</div>
              <div style={{ marginLeft: 10 }}>
                <SelectInput
                  title=""
                  value={this.state.dateFilter}
                  values={this.state.dateFilters}
                  onChange={(event: any) => this.handleDateChange(event.target.value)}
                />
              </div>
            </div>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Zip</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {this.state.contacts.map((contact, index) => (
                  <TableRow key={`contact-${index}`}>
                    <TableCell component="th" scope="row" style={styles.link} onClick={() => this.handleContactClick(contact)}>{contact.firstName} {contact.lastName}</TableCell>
                    <TableCell>{(contact.email ? contact.email : 'n/a')}</TableCell>
                    <TableCell>{contact.phoneNumber}</TableCell>
                    <TableCell>{contact.address}</TableCell>
                    <TableCell>{contact.city}</TableCell>
                    <TableCell>{contact.state}</TableCell>
                    <TableCell>{contact.zip}</TableCell>
                    <TableCell>{contact.status}</TableCell>
                    <TableCell>{moment(contact.createdDate).format('MM/DD/YYYY')}</TableCell>
                    <TableCell>
                      <IconButton aria-label="edit" onClick={() => this.handleOpenMessage(contact)} >
                        <LaunchIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {this.state.lastKey !== null &&
              <ActionButton name="LOAD MORE" onClick={() => this.fetchRecentContacts(this.state.dateFilter)} fullWidth />
            }
            <div style={styles.footer}>Showing {this.state.contacts.length}</div>
          </Loader>
          <Drawer anchor="right" open={this.state.showDrawer} onClose={() => this.toggleDrawer()}>
            <div style={{ padding: 20, width: 400 }}>
              <Loader open={this.state.loadingDrawer}>
                <div style={styles.messageDetails}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>Contact</Grid>
                    <Grid item xs={6}>{this.state.selectedContact.firstName} {this.state.selectedContact.lastName}</Grid>
                    <Grid item xs={6}>Phone</Grid>
                    <Grid item xs={6}>{this.state.selectedContact.phoneNumber}</Grid>
                    <Grid item xs={6}>Owner</Grid>
                    <Grid item xs={6}>Jon</Grid>
                  </Grid>
                </div>
                <div style={styles.messageContainer}>
                  {this.state.messages.map((message: Message, index) => (
                    <div style={(message.fromPhoneNumber === this.state.messageGroup.toPhoneNumber ? styles.toMessageContainer : styles.fromMessageContainer)}>
                      <div style={(message.toPhoneNumber === this.state.messageGroup.toPhoneNumber ? styles.toMessage : styles.fromMessage)}>{message.message}</div>
                      <div style={styles.messageDate}>{moment(message.createdDate).fromNow()}</div>
                    </div>
                  ))}
                </div>
              </Loader>
            </div>
          </Drawer>
        </Container>
      </AdminContainer>
    )
  }


}

const mapStateToProps = (state: any) => {
  return {
    auth: state.auth.auth,
    session: state.session.session,
    apiUrls: state.site.apiUrls
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return bindActionCreators({ updateSession, setApiUrls, addAuth, openSnackbar, closeSnackbar }, dispatch);
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter<any, any>(AdminPage));



// WEBPACK FOOTER //
// ./src/pages/admin/Admin.tsx